/**
 * State shape for the Get a Quote wizard.
 * Holds the current step and draft data that later slices will populate.
 */

import { UsStateCode } from '@/enterprise/components/InstantPay/usStates';
import { CreateMonduQuoteBody, MonduQuote } from '@/enterprise/data/useCreateMonduQuote';
import { ANNUAL_REVENUE_BANDS } from './content';

/** Each value is also the address segment that names the step. See `quoteWizardRouting.ts`. */
export type QuoteWizardStep = 'business-details' | 'add-customers' | 'review-quote';

/** Identifies the selected annual revenue band. Labels and figures live on `ANNUAL_REVENUE_BANDS`. */
export type AnnualRevenueRangeId = (typeof ANNUAL_REVENUE_BANDS)[number]['id'];

/**
 * Step 1 values, shaped so the quote request can be built from them without re-reading the form.
 */
export interface BusinessDetailsDraft {
  /** Prefilled from the logged-in user, but editable — the contact need not be the account holder. */
  firstName: string;
  lastName: string;
  emailAddress: string;
  /** As typed. Separators and country codes are kept; only the digit count is validated. */
  phoneNumber: string;
  /** Omitted from the quotes body when empty, so SEA falls back to the account company name. */
  companyName: string;
  addressLine1: string;
  city: string;
  /** Form only. The SEA quotes body has no state field. */
  state: UsStateCode;
  zipCode: string;
  /**
   * The selected band, and the only revenue input the form offers. `annualRevenueFigure` turns it
   * into the `revenue: number` the quotes body requires.
   */
  revenueRange: AnnualRevenueRangeId;
}

/**
 * Identifies the selected monthly invoice volume band. Labels live in `content.ts`, where the bands
 * are flagged as placeholders — storing the id means replacing them does not touch wizard state.
 */
export type MonthlyInvoiceVolumeId = 'LT-100K' | '100K-500K' | '500K-1M' | '1M-5M' | '5M-PLUS';

/**
 * One row of the step 2 Added Customers list. These stay in wizard state — the SEA quotes body has
 * no customers field, so nothing here is sent when the quote is generated.
 */
export interface AddedCustomer {
  /** Null when the name was typed instead of picked. Still a valid row — Mondu fuzzy-matches it. */
  entityId: string | null;
  /** What Mondu is asked to match, picked or typed. */
  name: string;
  /** Null for a typed name — the address line belongs to a catalog entity. */
  addressLine: string | null;
  /** Null when added without picking a band — volume is optional. */
  volumeId: MonthlyInvoiceVolumeId | null;
}

/**
 * Row identity, used for the React key, the duplicate check and removal. The normalized name rather
 * than the entity id, because a typed name has none.
 */
export const customerKey = (name: string): string => name.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * The figure for a band. Every id comes from `ANNUAL_REVENUE_BANDS`, and that table's `satisfies`
 * clause rejects a band with no figure, so this cannot return `undefined`.
 */
export const annualRevenueFigure = (rangeId: AnnualRevenueRangeId): number => {
  const band = ANNUAL_REVENUE_BANDS.find((candidate) => candidate.id === rangeId);
  if (!band) {
    throw new Error(`No revenue figure for band ${rangeId}`);
  }

  return band.figure;
};

/**
 * Draft → quotes request body. Customers, monthly volume and `state` are deliberately absent: the
 * SEA contract has no field for any of them.
 *
 * `registrationNumber` is absent for a different reason — the step stopped asking for it. SEA
 * distinguishes an omitted value from an empty one: omitting leaves the field off the ENF supplier
 * profile, whereas `''` is forwarded and stored as an empty string. Omitting is the honest shape,
 * since the supplier was never asked.
 */
export const buildCreateQuoteBody = (draft: BusinessDetailsDraft): CreateMonduQuoteBody => ({
  // Omitted rather than sent empty, so SEA falls back to the company name on the account.
  ...(draft.companyName.trim() ? { companyName: draft.companyName.trim() } : {}),
  revenue: annualRevenueFigure(draft.revenueRange),
  registeredAddress: {
    addressLine1: draft.addressLine1.trim(),
    city: draft.city.trim(),
    zipCode: draft.zipCode.trim(),
  },
});

export interface QuoteWizardState {
  step: QuoteWizardStep;
  businessDetails: BusinessDetailsDraft | null;
  customers: AddedCustomer[];
  /** The create-quote response, kept for Review Quote. Null until a `quoted` quote comes back. */
  quote: MonduQuote | null;
}

export const initialWizardState: QuoteWizardState = {
  step: 'business-details',
  businessDetails: null,
  customers: [],
  quote: null,
};
